import { Component, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { StorageService } from '../../../../../services/storage-service.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { timeout } from 'rxjs';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { GraphqlService } from 'src/app/services/graphql.service';
import { GET_CURRENT_SHOP_SUBSCRIPTION } from 'src/app/modules/billing/billing-service/billing.graphql';

// Same tier list as the backend's require_subscription_tier(["Premium",
// "Enterprise"]) gate on /store/chat/ (store/views.py) — kept in sync
// manually since there's no shared schema for it.
const CHAT_ALLOWED_TIERS = ['Premium', 'Enterprise'];

interface ChatMessage {
  id: string;
  text: string;
  html?: SafeHtml;
  sender: 'user' | 'bot';
  timestamp: Date;
}

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false
})
export class ChatWidgetComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatMessages') chatMessagesContainer: ElementRef;

  isExpanded = false;
  unreadCount = 0;
  messageInput = '';
  isLoading = false;
  messages: ChatMessage[] = [];

  // Gates the chat UI itself (shows an upgrade prompt instead of the normal
  // message thread) — the backend enforces the same tier requirement
  // independently, so this is a UX nicety, not the actual security boundary.
  isCheckingSubscription = true;
  isPremium = false;
  currentPlanName: string | null = null;

  private apiUrl = 'https://advancedstore.duckdns.org/store/chat/';
  private token: string = '';
  private shouldScroll = false;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private sanitizer: DomSanitizer,
    private spinner: SpinnerVisibilityService,
    private apollo: GraphqlService
  ) {}

  ngOnInit(): void {
    this.token = this.storageService.get('currentClient') || '';
    this.addMessage('Hello! Ask me about products, prices, or inventory.', 'bot');
    this.checkSubscription();
  }

  private async checkSubscription(): Promise<void> {
    this.isCheckingSubscription = true;
    try {
      const result: any = await this.apollo.fetchData({
        query: GET_CURRENT_SHOP_SUBSCRIPTION,
        variables: {},
      });
      const sub = result?.data?.getCurrentShopSubscription;
      this.currentPlanName = sub?.plan?.name ?? null;
      this.isPremium = !!sub?.isUsable && CHAT_ALLOWED_TIERS.includes(this.currentPlanName || '');
    } catch (error) {
      console.error('Error checking chat subscription tier:', error);
      // Fail closed on the UI check — the backend's own gate is the real
      // boundary, so this only affects whether the upgrade prompt shows.
      this.isPremium = false;
    } finally {
      this.isCheckingSubscription = false;
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  toggleChat(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.unreadCount = 0;
      this.shouldScroll = true;
    }
  }

  closeChat(): void {
    this.isExpanded = false;
  }

  async sendMessage(): Promise<void> {
    if (!this.messageInput.trim() || this.isLoading || !this.isPremium) return;

    const userMessage = this.messageInput.trim();
    this.messageInput = '';

    this.addMessage(userMessage, 'user');
    this.shouldScroll = true;
    this.isLoading = true;

    // Hide the global spinner for this chat request
    this.spinner.hide();

    try {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${this.token}`
      });

      const response: any = await this.http.post(this.apiUrl, { message: userMessage }, {
        headers: headers
      }).pipe(timeout(600000)).toPromise();

      const botResponse = response.chart_data || response.response || 'No response';
      this.addMessage(botResponse, 'bot');

      if (!this.isExpanded) this.unreadCount++;
      this.shouldScroll = true;

    } catch (error: any) {
      // DRF error responses from /store/chat/ use {"detail": ...} (including
      // the 402 "upgrade required" response from the tier gate) — message
      // was never the right key here.
      this.addMessage(error.error?.detail || 'Error occurred. Please try again.', 'bot');
      this.shouldScroll = true;
    } finally {
      this.isLoading = false;
      // Ensure spinner is hidden after request completes
      this.spinner.hide();
    }
  }

  private addMessage(text: string, sender: 'user' | 'bot'): void {
    const message: ChatMessage = {
      id: Date.now().toString() + Math.random(),
      text: text,
      sender: sender,
      timestamp: new Date()
    };

    // Convert markdown to HTML for bot messages
    if (sender === 'bot') {
      message.html = this.sanitizer.bypassSecurityTrustHtml(this.parseMarkdown(text));
    }

    this.messages.push(message);
  }

  private parseMarkdown(text: string): string {
    let html = text;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^\d+\. (.*$)/gim, '<li>$1</li>');

    // Wrap consecutive list items in ul
    html = html.replace(/(<li>.*<\/li>\n?)+/gim, '<ul>$&</ul>');

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');

    // Wrap in paragraph
    html = '<p>' + html + '</p>';

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
  }

  clearChat(): void {
    this.messages = [];
    this.addMessage('Hello! Ask me about products, prices, or inventory.', 'bot');
  }

  private scrollToBottom(): void {
    try {
      const element = this.chatMessagesContainer?.nativeElement;
      if (element) element.scrollTop = element.scrollHeight;
    } catch (e) {}
  }
}
